import { useEffect, useRef, useState } from "react";
import { usePhotoFeed } from "../../hooks/usePhotoFeed";
import { useAppConfig } from "../../hooks/useAppConfig";
import { useReloadOnToken } from "../../hooks/useReloadOnToken";
import { fetchGuestbook, type GuestbookEntry } from "../../api/guestbook";
import styles from "./KioskPage.module.css";

const LAST_PHOTO_ID_KEY = "mariage_kiosk_last_photo_id";
// Guestbook messages already shown on this TV. Kept in localStorage (not
// sessionStorage) so the periodic reload or a browser restart never replays them.
const SEEN_GUESTBOOK_KEY = "mariage_kiosk_seen_guestbook";
const GUESTBOOK_POLL_MS = 60_000;
const MAX_INTERLUDE_SECONDS = 30;

function loadSeenIds(): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_GUESTBOOK_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : []);
  } catch {
    return new Set();
  }
}

function saveSeenIds(ids: Set<string>) {
  try {
    localStorage.setItem(SEEN_GUESTBOOK_KEY, JSON.stringify([...ids]));
  } catch {
    /* storage unavailable: worst case a message can be shown again after a reload */
  }
}

export function KioskPage() {
  const config = useAppConfig();
  const { photos, loaded } = usePhotoFeed(config.galleryPollIntervalSeconds);
  // "Recharger le kiosk" in the admin Maintenance menu.
  useReloadOnToken(config.kioskReloadToken);
  const [activeIndex, setActiveIndex] = useState(0);
  // Bumped each time a slide (re)activates, so its <img>/<video> remounts and
  // the Ken Burns CSS animation restarts instead of staying frozen at its end state.
  const [activationTick, setActivationTick] = useState(0);
  const hasRestoredPosition = useRef(false);
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guestbook interludes: every kioskGuestbookIntervalSeconds a "round" becomes
  // pending; at the next slide change, up to kioskGuestbookMaxPerRound messages
  // that were never shown before are displayed one after the other, then the
  // slideshow continues. Shown ids are persisted so a message never repeats.
  const [interlude, setInterlude] = useState<GuestbookEntry | null>(null);
  const guestbookRef = useRef<GuestbookEntry[]>([]);
  const seenIdsRef = useRef<Set<string>>(loadSeenIds());
  const roundPendingRef = useRef(false);
  const roundQueueRef = useRef<GuestbookEntry[]>([]);
  const interludeActiveRef = useRef(false);
  const interludeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Mirrors `photos` for reading inside the timer effect below without
  // making that effect depend on the array reference itself (see why there).
  // Latest versions of the slide-advance functions, so timers set up earlier never call a stale copy.
  const latestRef = useRef({ advanceNow: () => {}, showNextInterlude: () => {} });
  const photosRef = useRef(photos);
  photosRef.current = photos;

  // On first load (including right after the periodic reload below), resume
  // from whichever photo was showing before instead of jumping back to #1.
  useEffect(() => {
    if (hasRestoredPosition.current || photos.length === 0) return;
    hasRestoredPosition.current = true;
    const lastId = sessionStorage.getItem(LAST_PHOTO_ID_KEY);
    const restoredIndex = lastId ? photos.findIndex((p) => p.id === lastId) : -1;
    setActiveIndex(restoredIndex >= 0 ? restoredIndex : 0);
  }, [photos]);

  // Keeps the index in range if the photo count shrinks (e.g. an admin
  // deletes something) out from under an index that's now out of bounds.
  useEffect(() => {
    if (photos.length === 0) return;
    setActiveIndex((i) => i % photos.length);
  }, [photos.length]);

  function showNextInterlude() {
    const next = roundQueueRef.current.shift();
    if (!next) {
      interludeActiveRef.current = false;
      setInterlude(null);
      advanceNow();
      return;
    }
    seenIdsRef.current.add(next.id);
    saveSeenIds(seenIdsRef.current);
    setInterlude(next);
    // Long messages stay longer so they can actually be read.
    const seconds = Math.min(MAX_INTERLUDE_SECONDS, Math.max(config.kioskGuestbookSeconds, 6 + next.message.length * 0.06));
    interludeTimer.current = setTimeout(() => latestRef.current.showNextInterlude(), seconds * 1000);
  }

  // Returns true when an interlude took over (the caller must not advance).
  function startInterludeRound(): boolean {
    roundPendingRef.current = false;
    const unseen = guestbookRef.current
      .filter((e) => !seenIdsRef.current.has(e.id))
      .sort((a, b) => a.createdAt - b.createdAt)
      .slice(0, config.kioskGuestbookMaxPerRound);
    if (unseen.length === 0) return false;
    roundQueueRef.current = unseen;
    interludeActiveRef.current = true;
    showNextInterlude();
    return true;
  }

  function advanceNow() {
    if (interludeActiveRef.current) return;
    if (roundPendingRef.current && startInterludeRound()) return;
    const list = photosRef.current;
    setActiveIndex((i) => (list.length > 0 ? (i + 1) % list.length : 0));
    setActivationTick((t) => t + 1);
  }
  latestRef.current = { advanceNow, showNextInterlude };

  // Each slide gets its own timer instead of one fixed-period interval:
  // photos advance after the configured slideshow interval, videos advance
  // after their own (usually shorter) cap - or sooner via the <video>'s
  // onEnded handler below if the clip is naturally shorter than the cap.
  useEffect(() => {
    if (photos.length === 0) return;
    const current = photosRef.current[activeIndex];
    if (!current) return;

    const ms =
      current.mediaType === "video" ? config.kioskVideoMaxSeconds * 1000 : config.slideshowIntervalSeconds * 1000;
    advanceTimer.current = setTimeout(() => latestRef.current.advanceNow(), ms);
    return () => {
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
    };
    // Deliberately depends on photos.length, not `photos` itself: usePhotoFeed's
    // poll produces a brand-new array reference every tick even when nothing
    // changed, which was resetting this timer before it ever fired (the real
    // bug that froze the kiosk on slide 1). The active slide's own content is
    // read fresh from photosRef instead.
  }, [activeIndex, photos.length, config.slideshowIntervalSeconds, config.kioskVideoMaxSeconds]);

  // Keeps the list of guestbook messages fresh (new ones can arrive all evening).
  useEffect(() => {
    let cancelled = false;
    async function poll() {
      try {
        const entries = await fetchGuestbook();
        if (!cancelled) guestbookRef.current = entries;
      } catch {
        /* keep the last known list */
      }
    }
    void poll();
    const interval = setInterval(poll, GUESTBOOK_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      // A tick that lands while messages are on screen is dropped: rounds never stack back to back.
      if (!interludeActiveRef.current) roundPendingRef.current = true;
    }, config.kioskGuestbookIntervalSeconds * 1000);
    return () => clearInterval(interval);
  }, [config.kioskGuestbookIntervalSeconds]);

  useEffect(
    () => () => {
      if (interludeTimer.current) clearTimeout(interludeTimer.current);
    },
    [],
  );

  // Keep the current photo's id saved continuously (not just before a timed
  // reload) so any unexpected reload also resumes in the right place.
  useEffect(() => {
    const current = photos[activeIndex];
    if (current) sessionStorage.setItem(LAST_PHOTO_ID_KEY, current.id);
  }, [photos, activeIndex]);

  // Periodic full reload keeps a TV left running for hours fresh (clears
  // accumulated JS state, picks up newly deployed versions). Position is
  // restored on the next mount via LAST_PHOTO_ID_KEY, so this never appears
  // to "restart" the slideshow from the guest's point of view.
  useEffect(() => {
    const interval = setInterval(() => window.location.reload(), config.kioskReloadIntervalSeconds * 1000);
    return () => clearInterval(interval);
  }, [config.kioskReloadIntervalSeconds]);

  if (loaded && photos.length === 0) {
    return (
      <div className={styles.empty}>
        <h1>{config.coupleNames}</h1>
        <p>Les premières photos vont bientôt apparaître ici ✨</p>
      </div>
    );
  }

  return (
    <div className={styles.stage} style={{ ["--slide-duration" as string]: `${config.slideshowIntervalSeconds}s` }}>
      {photos.map((photo, index) => {
        const isActive = index === activeIndex;
        const isVideo = photo.mediaType === "video";
        // Videos have no resized "medium" variant to blur into a backdrop -
        // the poster thumbnail is the only image available for those.
        const backdropUrl = isVideo ? photo.thumbUrl : photo.mediumUrl;
        return (
          <div key={photo.id} className={`${styles.slide} ${isActive ? styles.slideActive : ""}`}>
            {/* Blurred, screen-filling copy of the same photo so portrait shots
                never leave empty bars or need cropping to fill a landscape TV. */}
            <div
              className={styles.backdrop}
              style={{ backgroundImage: `url(${backdropUrl})` }}
              aria-hidden="true"
            />
            {isVideo ? (
              <video
                key={isActive ? `active-${activationTick}` : "idle"}
                className={styles.image}
                src={photo.originalUrl}
                poster={photo.thumbUrl}
                preload={isActive ? "auto" : "none"}
                autoPlay={isActive}
                muted
                playsInline
                onEnded={isActive ? advanceNow : undefined}
              />
            ) : (
              <img
                key={isActive ? `active-${activationTick}` : "idle"}
                className={`${styles.image} ${isActive ? styles.imageActive : ""}`}
                src={photo.mediumUrl}
                alt=""
                loading={isActive ? "eager" : "lazy"}
              />
            )}
            {isActive && photo.uploaderName && <p className={styles.caption}>Par {photo.uploaderName}</p>}
          </div>
        );
      })}
      {interlude && (
        <div className={styles.interlude} key={interlude.id}>
          <div className={styles.interludeCard}>
            <p className={styles.interludeLabel}>📖 Livre d'or</p>
            <p
              className={[
                styles.interludeMessage,
                interlude.message.length > 350
                  ? styles.interludeMessageLong
                  : interlude.message.length > 140
                    ? styles.interludeMessageMedium
                    : "",
              ].join(" ")}
            >
              {interlude.message}
            </p>
            <p className={styles.interludeName}>— {interlude.name}</p>
          </div>
        </div>
      )}
    </div>
  );
}
